import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import {
  FieldType,
  IFieldFieldsFragment,
  IRelationalOperatorFieldsFragment,
  RelationalOperatorKey,
} from '../../../../../../../utils/apollo/graphqlSchema';
import useFilterWidgetFormContext from '../../../../../utils/useFilterWidgetFormContext';
import DateTypeOperands from './DateTypeOperands';
import PredicateRelValueMenu from './PredicateRelValueMenu';
import CreatableOperandMenu from './CreatableOperandMenu';
import OperandValuesBetween from './OperandValuesBetween';
import {
  defaultAnyFieldValue,
  IPredicateFormFieldPath,
} from '../../../../../types';
import { getPredicateFormFieldPath } from '../../../../../utils/formPath';
import { isEmpty } from 'lodash';
import { useFilterWidgetPropContext } from '../../../../../FilterWidgetPropContext';
import { getFieldValueKey } from '../../../../../../../utils/analytics/events/filter/utils';

interface IProps {
  selectedField: IFieldFieldsFragment;
  selectedRelOp: IRelationalOperatorFieldsFragment;
  formPath: IPredicateFormFieldPath;
}

const DATE_FIELD_TYPES = [
  FieldType.Date,
  FieldType.Week,
  FieldType.Month,
  FieldType.Year,
  FieldType.Timestamp,
  FieldType.Datetime,
];

const OperandValues = ({ selectedField, selectedRelOp, formPath }: IProps) => {
  const { includeAnyValue } = useFilterWidgetPropContext();
  const { control, setValue, getValues } = useFilterWidgetFormContext();

  const fieldPath = getPredicateFormFieldPath(formPath);

  const isBetween = selectedRelOp.key === RelationalOperatorKey.Between;
  const isDateType = DATE_FIELD_TYPES.includes(selectedField.type);

  const placeholder = selectedField.valueHint || 'Value';

  // If includeAnyValue is true and the operand is empty, set the operand to ['Any']
  useEffect(() => {
    if (includeAnyValue && !isDateType) {
      const operandPath = `${fieldPath}.operand.operands` as const;
      const currentValue = getValues(operandPath);

      if (!currentValue || isEmpty(currentValue)) {
        setValue(operandPath, [defaultAnyFieldValue], { shouldValidate: true });
      }
    }
  }, [includeAnyValue, fieldPath, setValue, getValues, isDateType]);

  // Case 1: User should select a preset value
  if (selectedRelOp?.values?.length > 0) {
    return (
      <Controller
        name={`${fieldPath}.operand.operands`}
        control={control}
        render={({ field: { onChange, value }, fieldState }) => (
          <PredicateRelValueMenu
            value={value?.length ? getFieldValueKey(value[0]) : ''}
            relOpValues={selectedRelOp.values}
            onChange={(newValue) => onChange(newValue ? [newValue] : [])}
            placeholder={placeholder}
            autoFocus={!value?.length}
            hasError={Boolean(fieldState.error)}
          />
        )}
      />
    );
  }

  // Case 2: date field types
  if (isDateType) {
    return (
      <DateTypeOperands
        formPath={formPath}
        selectedField={selectedField}
        placeholder={placeholder}
        isBetween={isBetween}
      />
    );
  }

  // Case 3: Between operator
  if (isBetween) {
    return (
      <OperandValuesBetween formPath={formPath} placeholder={placeholder} />
    );
  }

  // Case 4: Eventually use creatable multi select menu for field values
  return (
    <Controller
      name={`${fieldPath}.operand.operands`}
      control={control}
      render={({ field: { onChange, value }, fieldState }) => (
        <CreatableOperandMenu
          selectedField={selectedField}
          operands={value ?? []}
          onChange={onChange}
          placeholder={placeholder}
          autoFocus={!value?.length || includeAnyValue}
          hasError={Boolean(fieldState.error)}
          includeAnyValue={includeAnyValue}
        />
      )}
    />
  );
};

export default OperandValues;
